import {isBefore, startOfDay} from 'date-fns';
import {z} from 'zod';

import {isNil, isNotNil, keys} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  CampaignDiscountTypeSchema,
  CampaignPromoPhotoSchema,
  CampaignFilterSchema,
} from '@dms/api/sales';
import i18n from '@dms/i18n';

const MIN_DISCOUNT_AMOUNT = 0;

const MAX_PERCENT_DISCOUNT_AMOUNT = 100;

const VISUAL_ELEMENT_FIELDS = ['watermarkId', 'promoPhotos'] as const;

const DESCENDING_RANGE_FIELDS = [
  ['priceFrom', 'priceTo'],
  ['mileageFrom', 'mileageTo'],
  ['yearFrom', 'yearTo'],
] as const;

const FILTER_FIELDS = keys(CampaignFilterSchema.shape);

export const campaignFormSchema = z
  .object({
    name: z.string().min(1, i18n.t('general.validations.fieldIsRequired')),
    ownerId: z.string(),
    validFrom: z.date(),
    validTo: z.date(),
    watermarkId: z.string().nullish(),
    promoPhotos: z.array(CampaignPromoPhotoSchema).optional(),
    warrantyProgramId: z.string().nullish(),
    discount: z
      .object({
        type: CampaignDiscountTypeSchema.nullish(),
        amount: z
          .number()
          .min(
            MIN_DISCOUNT_AMOUNT,
            i18n.t('general.validations.minValue', {min: MIN_DISCOUNT_AMOUNT})
          )
          .nullish(),
      })
      .nullish(),
    keepExistingPromoPhotos: z.boolean().optional(),
    isAppliedToAllVehicles: z.boolean().optional(),
    filter: CampaignFilterSchema.optional(),
    manuallySelectedVehicles: z
      .array(z.object({vehicleId: z.string(), isIncluded: z.boolean()}))
      .nullish(),
  })
  .superRefine((values, ctx) => {
    if (isBefore(startOfDay(values.validTo), startOfDay(values.validFrom))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['validTo'],
        message: i18n.t('entity.salesCampaign.validation.validToAfterFrom'),
      });
    }
    // A Campaign needs at least one visual element
    if (isNilOrEmpty(values.watermarkId) && isNilOrEmpty(values.promoPhotos)) {
      VISUAL_ELEMENT_FIELDS.forEach((field) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: i18n.t('entity.salesCampaign.validation.visualRequired'),
        });
      });
    }
    // A discount is optional, but once a type is chosen an amount must follow it and vice versa.
    if (isNotNil(values.discount?.type) && isNil(values.discount?.amount)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['discount', 'amount'],
        message: i18n.t('general.validations.fieldIsRequired'),
      });
    }
    if (isNil(values.discount?.type) && isNotNil(values.discount?.amount)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['discount', 'type'],
        message: i18n.t('general.validations.fieldIsRequired'),
      });
    }
    if (
      values.discount?.type === CampaignDiscountTypeSchema.enum.PERCENT &&
      isNotNil(values.discount?.amount) &&
      values.discount.amount > MAX_PERCENT_DISCOUNT_AMOUNT
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['discount', 'amount'],
        message: i18n.t('general.validations.maxValue', {max: MAX_PERCENT_DISCOUNT_AMOUNT}),
      });
    }
    // If the campaign is not applied to all vehicles, at least one filter must be selected.
    if (!values.isAppliedToAllVehicles) {
      if (FILTER_FIELDS.every((field) => isNilOrEmpty(values?.filter?.[field]))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['isAppliedToAllVehicles'],
          message: i18n.t('entity.salesCampaign.validation.vehicleSelectionRequired'),
        });
      }
      DESCENDING_RANGE_FIELDS.forEach(([fromField, toField]) => {
        const from = values?.filter?.[fromField];
        const to = values?.filter?.[toField];

        if (isNotNil(from) && isNotNil(to) && to < from) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['filter', toField],
            message: i18n.t('general.validations.fromTimeMustBeBeforeToTime'),
          });
        }
      });
    }
  });

export type CampaignFormValues = z.infer<typeof campaignFormSchema>;
